import { useEffect } from 'react';
import { Route, Switch } from 'wouter';
import { Taskpane } from '../pages/Taskpane';
import Login from '../pages/Login';
import { useOfficeStore } from '../officeStore';
import '../index.css';
import * as Sentry from '@sentry/react';
import { API_BASE_URL, ENVIRONMENT, SENTRY_DSN } from '../config';
import { BrandVariants, FluentProvider, createLightTheme } from '@fluentui/react-components';
import { useIntl } from 'react-intl';
import Intl from '../Intl';
import messages from '../messages';
import { Provider } from 'use-http';
import { useFetchOptions } from '../hooks/useFetchOptions';
import { AuthenticationProvider } from './auth/AuthenticationProvider';
import { ErrorBoundary } from './ErrorBoundary';
import {
  scrubAuthParamsIntegration,
  scrubBreadcrumb,
  scrubEvent,
  scrubRecordingEvent,
  scrubSpan,
} from '../utils/sentryScrub';

const brandVariants: BrandVariants = { 
  10: "#020305",
  20: "#101820",
  30: "#162838",
  40: "#19344C",
  50: "#1B4160",
  60: "#1C4E75",
  70: "#1C5C8B",
  80: "#1A6AA1",
  90: "#1579B8",
  100: "#3A86C3",
  110: "#5894CA",
  120: "#72A1D1",
  130: "#89AFD8",
  140: "#9FBDE0",
  150: "#B5CCE7",
  160: "#CADAEE",
};

const theme = createLightTheme(brandVariants);

Sentry.init({
  dsn: SENTRY_DSN,
  tracesSampleRate: 1.0,
  environment: ENVIRONMENT,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,

  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,

  // Login credentials and the email can travel in URLs (/login?code=&state=,
  // the legacy ?token=, /login?email=). See utils/sentryScrub.ts.
  beforeSend: event => scrubEvent(event),
  beforeSendTransaction: event => scrubEvent(event),
  beforeSendSpan: span => scrubSpan(span),
  beforeBreadcrumb: breadcrumb => scrubBreadcrumb(breadcrumb),

  integrations: [
    scrubAuthParamsIntegration(),
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
      stickySession: true,
      beforeAddRecordingEvent: event => scrubRecordingEvent(event),
    }),
    Sentry.captureConsoleIntegration({
      levels: ['error'],
    }),
  ],
});

interface IAppProps {
  officeInitialized: boolean;
  language: string;
}

export const App: React.FC<IAppProps> = (props: IAppProps): JSX.Element => {
  const { load } = useOfficeStore(({ isOfficeInitialized, load }) => ({
    isOfficeInitialized,
    load,
  }));

  useEffect(() => {
    load(props.officeInitialized);
  }, [load, props.officeInitialized]);

  return (
    <ErrorBoundary>
      <FluentProvider
        theme={theme}
        style={{
          height: 'calc(100% - 22px)',
          margin: 22,
          marginTop: 0,
        }}>
        <Intl language={props.language}>
          <Routes />
        </Intl>
      </FluentProvider>
    </ErrorBoundary>
  );
};

const Routes = () => {
  const intl = useIntl();
  const fetchOptions = useFetchOptions();

  return (
    <>
      <Switch>
        <Route path="/login" component={Login} />
        <AuthenticationProvider>
          <Provider url={API_BASE_URL()} options={fetchOptions}>
            <Route path="/taskpane" component={Taskpane} />
          </Provider>
        </AuthenticationProvider>
        <Route>{intl.formatMessage(messages.fourOhFourMessage)}</Route>
      </Switch>
    </>
  );
};

export default App;
